import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import ExpandMoreOutlined from "@mui/icons-material/ExpandMoreOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import SimplePagination from "../../components/SimplePagination";
import {
  LIBRARY_KPIS,
  LIBRARY_TABLE_COLUMNS,
  PUBLISHED_ONTOLOGIES,
  PREVIOUSLY_CREATED_ONTOLOGIES,
  LibraryOntologyStatus
} from "../../data/ontology-library.data";
import "./OntologyLibrary.scss";

type LibraryTab = "published" | "previous";

const TABLE_PAGE_SIZE = 5;

const STATUS_CLASS: Record<LibraryOntologyStatus, string> = {
  Published: "library-status-pill--published",
  Active: "library-status-pill--active"
};

const KPI_ICONS: Record<string, React.ReactNode> = {
  "published-core-models": <CheckCircleOutlined fontSize="small" />,
  "active-studio-initiatives": <AccountTreeOutlined fontSize="small" />
};

const OntologyLibrary: React.FC = () => {
  const [activeTab, setActiveTab] = useState<LibraryTab>("published");
  const [search, setSearch] = useState("");
  const [businessUnitFilter, setBusinessUnitFilter] = useState("All");
  const [teamFilter, setTeamFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  const rows =
    activeTab === "published"
      ? PUBLISHED_ONTOLOGIES
      : PREVIOUSLY_CREATED_ONTOLOGIES;

  const buildOptions = (values: string[]) => [
    "All",
    ...Array.from(new Set(values)).sort()
  ];
  const businessUnitOptions = useMemo(
    () => buildOptions(rows.map((row) => row.domainBusinessUnit)),
    [rows]
  );
  const teamOptions = useMemo(
    () => buildOptions(rows.map((row) => row.teamProject)),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (searchTerm && !row.name.toLowerCase().includes(searchTerm)) {
        return false;
      }
      if (
        businessUnitFilter !== "All" &&
        row.domainBusinessUnit !== businessUnitFilter
      ) {
        return false;
      }
      if (teamFilter !== "All" && row.teamProject !== teamFilter) {
        return false;
      }
      return true;
    });
  }, [rows, search, businessUnitFilter, teamFilter]);

  const totalRows = filteredRows.length;
  const paginatedRows = useMemo(
    () =>
      filteredRows.slice(
        (currentPage - 1) * TABLE_PAGE_SIZE,
        currentPage * TABLE_PAGE_SIZE
      ),
    [filteredRows, currentPage]
  );

  const handleTabChange = (tab: LibraryTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const productionReadyCount = rows.filter(
    (row) => row.status === "Published"
  ).length;

  return (
    <div className="library-container">
      <nav className="library-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Engineering Information Management
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <Link to="/create-ontology" className="breadcrumb-link">
          Ontology Studio
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Ontology Library</span>
      </nav>

      <div className="library-page-header">
        <div className="library-page-header__title-row">
          <h1 className="library-page-title">Ontology Library</h1>
          <span className="library-help-badge" title="Ontology library help">
            ?
          </span>
        </div>
        <p className="library-page-description">
          Browse and discover validated enterprise core models, or explore
          previously created domain ontology initiatives. Access schema version
          history, reference standards, and model boundaries.
        </p>
      </div>

      <section className="library-kpis">
        {LIBRARY_KPIS.map((kpi) => (
          <div className="library-kpi-card" key={kpi.id}>
            <div className="library-kpi-card__top">
              <span className="library-kpi-card__label">{kpi.label}</span>
              <span className="library-kpi-card__icon">
                {KPI_ICONS[kpi.id]}
              </span>
            </div>
            <div className="library-kpi-card__value-row">
              <span className="library-kpi-card__value">{kpi.value}</span>
              <span className="library-kpi-card__delta">{kpi.delta}</span>
            </div>
            <div className="library-kpi-card__helper">
              <span className="library-kpi-card__helper-muted">
                {kpi.helperLine1}
              </span>
              <span className="library-kpi-card__helper-dark">
                {kpi.helperLine2}
              </span>
            </div>
          </div>
        ))}
      </section>

      <section className="library-workspace-card">
        <div className="library-workspace-card__header">
          <div className="library-workspace-card__header-top">
            <div className="library-tabs">
              <button
                type="button"
                className={`library-tabs__tab${
                  activeTab === "published" ? " library-tabs__tab--active" : ""
                }`}
                onClick={() => handleTabChange("published")}
              >
                Published Ontologies
              </button>
              <button
                type="button"
                className={`library-tabs__tab${
                  activeTab === "previous" ? " library-tabs__tab--active" : ""
                }`}
                onClick={() => handleTabChange("previous")}
              >
                Previously Created Ontologies
              </button>
            </div>
            <div className="library-workspace-card__tools">
              <div className="library-search">
                <SearchIcon className="library-search__icon" fontSize="small" />
                <input
                  type="text"
                  className="library-search__input"
                  placeholder="Search library..."
                  value={search}
                  onChange={(event) => handleSearchChange(event.target.value)}
                />
              </div>
              <button
                type="button"
                className="library-btn library-btn--secondary"
              >
                <TuneOutlined fontSize="small" />
                <span>More Filters</span>
              </button>
            </div>
          </div>

          <div className="library-workspace-card__header-bottom">
            <span className="library-pill">
              <span className="library-pill__dot" />
              {productionReadyCount} Production Ready
            </span>
            <div className="library-table-filters">
              <label className="library-table-filter">
                <span className="library-table-filter__label">
                  Business Unit
                </span>
                <div className="library-table-filter__control">
                  <select
                    value={businessUnitFilter}
                    onChange={(event) => {
                      setBusinessUnitFilter(event.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    {businessUnitOptions.map((option) => (
                      <option key={option} value={option}>
                        {option === "All" ? "All Business Units" : option}
                      </option>
                    ))}
                  </select>
                  <ExpandMoreOutlined
                    className="library-table-filter__chevron"
                    fontSize="small"
                  />
                </div>
              </label>
              <label className="library-table-filter library-table-filter--team">
                <span className="library-table-filter__label">
                  Team / Project Group
                </span>
                <div className="library-table-filter__control">
                  <select
                    value={teamFilter}
                    onChange={(event) => {
                      setTeamFilter(event.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    {teamOptions.map((option) => (
                      <option key={option} value={option}>
                        {option === "All" ? "All Teams" : option}
                      </option>
                    ))}
                  </select>
                  <ExpandMoreOutlined
                    className="library-table-filter__chevron"
                    fontSize="small"
                  />
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="library-table-scroll">
          <table className="library-table">
            <thead>
              <tr>
                {LIBRARY_TABLE_COLUMNS.map((column) => (
                  <th key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedRows.length === 0 && (
                <tr>
                  <td
                    colSpan={LIBRARY_TABLE_COLUMNS.length}
                    className="library-table__muted"
                  >
                    No ontologies found.
                  </td>
                </tr>
              )}
              {paginatedRows.map((row) => (
                <tr key={row.id}>
                  <td className="library-table__name">{row.name}</td>
                  <td>{row.version}</td>
                  <td>{row.domainBusinessUnit}</td>
                  <td>{row.teamProject}</td>
                  <td className="library-table__muted">{row.lastUpdated}</td>
                  <td>
                    <span className="library-stage-pill">
                      {row.lifecycleStage}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`library-status-pill ${
                        STATUS_CLASS[row.status]
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td>
                    <button type="button" className="library-view-btn">
                      View / Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="library-table-footer">
          <span className="library-table-footer__info">
            Showing{" "}
            {paginatedRows.length === 0
              ? 0
              : (currentPage - 1) * TABLE_PAGE_SIZE + 1}
            -{(currentPage - 1) * TABLE_PAGE_SIZE + paginatedRows.length} of{" "}
            {totalRows} registered library ontologies
          </span>
          {totalRows > TABLE_PAGE_SIZE && (
            <SimplePagination
              currentPage={currentPage}
              totalPages={Math.max(1, Math.ceil(totalRows / TABLE_PAGE_SIZE))}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </section>
    </div>
  );
};

export default OntologyLibrary;
